/**
 * 09 §5.2 contextual first-time prompts: once each (persisted in
 * `onboarding.prompts`), never during COUNTDOWN/ROUND_END, dismissed on the
 * listed condition or after 8 s. Pure apart from the store.
 */
import type { MatchPhase, PlayerStateName } from '../../../sim/types.js'
import type { SimEvent } from '../../../shared/events.js'
import type { OptionsStore } from '../options.js'

export const PROMPT_MS = 8000

/** Seconds since the last local input (movement/throw/shield/dash/key), for
 *  `hubIdleS`. Reset on hub entry so the `spectate` prompt measures idle time
 *  in the current hub visit only. */
export class IdleTimer {
  private lastActivity = 0
  reset(now: number): void { this.lastActivity = now }
  activity(now: number): void { this.lastActivity = now }
  idleS(now: number): number { return Math.max(0, (now - this.lastActivity) / 1000) }
}

export interface PromptCtx {
  now: number
  hub: boolean
  phase: MatchPhase | null
  queueOff: boolean
  othersFlagged: number
  hubIdleS: number
  hubTimeS: number
  enemyGroundDiscNear: boolean
  events: SimEvent[]
  meState: PlayerStateName | null
  heldCount: number
  liveMatchExists: boolean
  localId: string
  /** Input-driven dismissals since last evaluate. */
  pressed?: { queue?: boolean; spectate?: boolean }
  lobbyPick?: boolean
  lobbyLocked?: boolean
}

export interface PromptDef { id: string; text: string; trigger: (c: PromptCtx) => boolean; dismiss: (c: PromptCtx) => boolean }

export const PROMPTS: PromptDef[] = [
  { id: 'queue', text: '[TAB] toggles the match queue', trigger: (c) => c.hub && c.hubTimeS >= 20 && c.queueOff && c.othersFlagged >= 1, dismiss: (c) => !!c.pressed?.queue || !c.queueOff },
  { id: 'steal', text: 'walk over a loose disc to take it', trigger: (c) => c.enemyGroundDiscNear, dismiss: (c) => c.events.some((e) => e.type === 'pickup' && e.playerId === c.localId) },
  { id: 'exhaust', text: 'shield drains stamina — it shares a pool with dash', trigger: (c) => c.events.some((e) => e.type === 'exhaust' && e.playerId === c.localId), dismiss: () => false },
  { id: 'pick', text: 'pick 3 · then lock 1 · your pick is worn on your chest', trigger: (c) => c.phase === 'LOBBY_PICK' || !!c.lobbyPick, dismiss: (c) => !!c.lobbyLocked || c.phase === 'REVEAL' },
  { id: 'chest', text: 'read enemy chest plates — their power-up is public', trigger: (c) => !c.hub && c.phase === 'LIVE', dismiss: () => false },
  { id: 'overtime', text: 'the deck closes in — stay inside the dashed bound', trigger: (c) => c.events.some((e) => e.type === 'overtime'), dismiss: () => false },
  { id: 'spectate', text: '[Q] spectate a live match', trigger: (c) => c.hub && c.liveMatchExists && c.hubIdleS >= 15, dismiss: (c) => !!c.pressed?.spectate },
  { id: 'twin', text: 'second disc: throws alternate · catches need a free slot', trigger: (c) => c.heldCount >= 2, dismiss: () => false },
]

export interface ActivePrompt { id: string; text: string; shownAt: number }

export class PromptEngine {
  active: ActivePrompt | null = null
  constructor(private readonly store: OptionsStore, private readonly defs: PromptDef[] = PROMPTS) {}

  private seen(id: string): boolean { return !!this.store.get('onboarding.prompts')[id] }

  /** Returns the prompt to show this frame (or null). */
  evaluate(c: PromptCtx): ActivePrompt | null {
    const blocked = c.phase === 'COUNTDOWN' || c.phase === 'ROUND_END'
    if (this.active) {
      const def = this.defs.find((d) => d.id === this.active!.id)!
      if (c.now - this.active.shownAt >= PROMPT_MS || def.dismiss(c) || blocked) this.active = null
      else return this.active
    }
    if (blocked) return null
    for (const d of this.defs) {
      if (this.seen(d.id)) continue
      if (d.trigger(c)) {
        this.store.dismissPrompt(d.id) // recorded as shown-once at trigger time
        this.active = { id: d.id, text: d.text, shownAt: c.now }
        return this.active
      }
    }
    return null
  }
}
